import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

import PackageDeliveryTypeApi from "Services/PackageDeliveryType";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PackageDeliveryTypeUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [imagePreview, setImagePreview] = useState(null);
  const [initialData, setInitialData] = useState({
    packageName: "",
    packageImage: null,
  });

  const fetchPackageDetails = useCallback(() => {
    if (!id) return;
    dispatch(showLoader("Fetching details..."));
    PackageDeliveryTypeApi.getPackageDeliveryTypeById(id)
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true && res?.data?.data) {
          const pkg = res?.data?.data;
          setInitialData({
            packageName: pkg.packageName || "",
            packageImage: null,
          });
          setImagePreview(pkg.packageImage || null);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch details",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      });
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog && id) {
      fetchPackageDetails();
    }
  }, [openDialog, id, fetchPackageDetails]);

  const maxFileSize = 5242880;

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("packageImage", file);
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleUpdatePackage = (formValues) => {
    dispatch(showLoader("Updating please wait..."));

    const formData = new FormData();
    formData.append("packageName", formValues.packageName.trim());
    if (formValues.packageImage) {
      formData.append("packageImage", formValues.packageImage);
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Package Delivery Type Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        handleModalClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error updating Package Delivery Type",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    PackageDeliveryTypeApi.updatePackageDeliveryType(id, formData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: yup.object({
      packageName: yup.string().required("Package Name is required"),
    }),
    onSubmit: handleUpdatePackage,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setImagePreview(null);
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.headerBar}>
        <Typography className={classes.headerTitle}>Edit Package Delivery Type</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeIconBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.contentContainer}>
        <Typography className={classes.fieldLabel}>Package Image</Typography>
        <Box {...getRootProps()} onClick={open} className={classes.uploadBox}>
          <input {...getInputProps()} />
          {imagePreview ? (
            <Box component="img" src={imagePreview} alt="Preview" className={classes.previewImage} />
          ) : (
            <>
              <FileUploadOutlinedIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadText}>Click to change package image</Typography>
            </>
          )}
        </Box>

        <Typography className={classes.fieldLabel}>Package Name</Typography>
        <TextField
          name="packageName"
          placeholder="e.g. Small Envelope, Medium Box, Heavy Crate"
          value={formik.values.packageName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors.packageName && formik.touched.packageName)}
          helperText={formik.touched.packageName && formik.errors.packageName}
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Box className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleModalClose} className={classes.cancelBtn}>
            Close
          </Button>
          <Button variant="contained" onClick={formik.handleSubmit} className={classes.submitBtn}>
            Update Package
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PackageDeliveryTypeUpdate;
